<template>
	<Card :title="trans('enrollments.payments.title')" :subtitle="trans('enrollments.payments.subtitle')">
		<div v-if="event.has_payment_providers || debitPaymentLink || creditPaymentLink" class="flex flex-col md:flex-row items-stretch md:items-center gap-3">
			<a v-if="event.has_mercado_pago" :href="route('payments.pay', {type: 'mercadopago', enrollment: enrollment.id})" target="_blank">
				<img class="w-full md:w-auto h-12 object-contain object-center rounded bg-gray-100 hover:bg-gray-200 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer p-2" :src="storage('logos/payment-providers/mercadopago.svg')" alt="MercadoPago">
			</a>
			<a v-if="event.has_pay_pal" :href="route('payments.pay', {type: 'paypal', enrollment: enrollment.id})" target="_blank">
				<img class="w-full md:w-auto h-12 object-contain object-center rounded bg-gray-100 hover:bg-gray-200 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer p-2" :src="storage('logos/payment-providers/paypal.svg')" alt="PayPal">
			</a>
			<a v-if="event.has_santander" :href="route('payments.pay', {type: 'santander', enrollment: enrollment.id})" target="_blank">
				<img class="w-full md:w-auto h-12 object-contain object-center rounded bg-gray-100 hover:bg-gray-200 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer p-2" :src="storage('logos/payment-providers/santander.svg')" alt="Santander">
			</a>
			<a v-if="debitPaymentLink" :href="debitPaymentLink" target="_blank" class="w-full md:w-auto h-12 rounded bg-gray-100 hover:bg-gray-200 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer p-2 flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
				<i class="fa-solid fa-credit-card text-3xl"/>
				<span>{{ trans('enrollments.payments.debit') }}</span>
			</a>
			<a v-if="creditPaymentLink" :href="creditPaymentLink" target="_blank" class="w-full md:w-auto h-12 rounded bg-gray-100 hover:bg-gray-200 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer p-2 flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
				<i class="fa-solid fa-credit-card text-3xl"/>
				<span>{{ trans('enrollments.payments.credit') }}</span>
			</a>
		</div>
		<div v-if="event.has_payment_providers && event.has_bank_accounts" class="flex items-center gap-2 text-gray-400">
			<hr class="grow">
			<p class="uppercase">{{ trans('general.or') }}</p>
			<hr class="grow">
		</div>
		<div class="flex flex-col gap-4">
			<div v-for="bankAccount in bankAccounts" :key="`bank-account-${bankAccount.id}`" class="px-3 py-2 border-l-4 border-gray-200 dark:border-gray-800 flex flex-col gap-2">
				<p class="font-bold">{{ bankAccount.bank }}</p>
				<LabeledObject>
					<template #label>{{ trans('events.bank_accounts.owner') }}</template>
					<p>{{ bankAccount.owner }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.bank_accounts.number') }}</template>
					<p>{{ bankAccount.number }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.bank_accounts.alias') }}</template>
					<p>{{ bankAccount.alias }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.bank_accounts.type') }}</template>
					<p>{{ trans(`enums.bank_account.${bankAccount.type}`) }}</p>
				</LabeledObject>
			</div>
		</div>
	</Card>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {storage} from '@/helpers'
import {trans} from 'laravel-vue-i18n'
import {ref, type Ref, defineProps} from 'vue'
import Card from '@/Components/Cards/Card.vue'
import {type Event} from '@/Types/Events/Event'
import LabeledObject from '@/Components/LabeledObject.vue'
import {type BankAccount} from '@/Types/Events/Payments/BankAccount'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'

const props = defineProps<{
	event:Event,
	enrollment:Enrollment,
	debitPaymentLink:string|null,
	creditPaymentLink:string|null,
	bankAccounts:Array<BankAccount>,
}>()

const event:Ref<Event> = ref(props.event)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const bankAccounts:Ref<Array<BankAccount>> = ref(props.bankAccounts)
const debitPaymentLink:Ref<string|null> = ref(props.debitPaymentLink)
const creditPaymentLink:Ref<string|null> = ref(props.creditPaymentLink)
</script>